import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import $api from "@/zapi-frontend/shared/api/axios";
import { Plus, RotateCw } from "lucide-react";
import { AppButton, AppSearchInput, FigmaTypography, ConfirmationModal } from "@libs/zapi-ui/custom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getRouteCreateProject } from "@/zapi-frontend/shared/const/router";
import { useGetProjectsList, GET_PROJECTS_LIST_KEY } from "@/zapi-frontend/shared/services/useGetProjectsList";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useProjectDimensions } from "@/zapi-frontend/shared/services/useProjectDimensions";
import { updateCustomSnippetsUrl } from "@/zapi-frontend/pages/SettingsPage/services/updateCustomSnippetsUrl";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { defineIsInvalidUrlError } from "../lib/defineIsInvalidUrlError";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { ProjectsCards } from "./ProjectsCards";
import { hasEditAccess, hasAdminPrivileges } from "@/zapi-frontend/shared/utils/permissions";
import { useOrganizationStore } from "@/zapi-frontend/stores/organization";
import { DuplicateProjectModal } from "@/zapi-frontend/widgets/Header/ui/DuplicateProjectModal";
import { sortKeyToField, type TSystemColumn } from "../lib/portfolioStorage";
import { usePortfolioDerivedList } from "../lib/usePortfolioDerivedList";
import { usePortfolioPrefs, filterKeyFromUrl } from "../lib/usePortfolioPrefs";
import { ProjectListSidebar } from "./ProjectListV2/_ProjectListSidebar";
import { ChatPanel } from "./ProjectListV2/_ChatPanel";
import { ChatFab } from "@/zapi-frontend/shared/ui/ResponsiveChatPanel";
import { useIsTabletLayout } from "../lib/useIsTabletLayout";
import {
  countVisibleAssistantMessages,
  useChatUnreadIndicator
} from "@/zapi-frontend/shared/hooks/useChatUnreadIndicator";
import { useProjectsListChatStore } from "@/zapi-frontend/stores/projectsListChatStore";
import { defaultSortDir } from "../lib/sortOptions";
import { EmptyOrgState } from "./ProjectListV2/_EmptyOrgState";
import { NoProjectsFoundState } from "./ProjectListV2/_NoProjectsFoundState";
import { PortfolioInvalidUrlState } from "./_PortfolioInvalidUrlState";
import { SortPopover } from "./SortFilterPopover/SortPopover";
import { FilterToggleButton } from "./SortFilterPopover/FilterToggleButton";
import { PropertiesPopover } from "./PropertiesPopover";
import { SavedFiltersControl } from "./SavedFiltersControl/SavedFiltersControl";
import { SaveCurrentFiltersButton } from "./SavedFiltersControl/SaveCurrentFiltersButton";
import { DimensionChipsRow } from "./DimensionChipsRow";
import { useSavedFilters } from "@/zapi-frontend/shared/services/useSavedFilters";
import { findMatchingSavedFilter, isFiltersEmpty } from "../lib/matchSavedFilter";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { CommitHash } from "@/zapi-frontend/shared/ui/custom/CommitHash";
import { useProjectVersionsStore } from "../model/projectVersionsStore";
import { LS_PORTFOLIO_SIDEBAR_COLLAPSED } from "@/zapi-frontend/shared/local-storage/keys";

export function ProjectsPage() {
  return <ProjectsPagePortfolio />;
}

function ProjectsPagePortfolio() {
  const { t } = useTranslation(["projects", "common"]);
  const { setProjects, projects, archiveProject, duplicateProject } = useProjectStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("search") ?? "";
  const { setMe, role, accessMode, isLabs } = useMeStore();
  const currentOrganizationId = useOrganizationStore((s) => s.currentOrganizationId);
  const hasEdit = hasEditAccess(role, accessMode);
  const isTabletLayout = useIsTabletLayout();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window !== "undefined" && window.matchMedia("(max-width: 1331px)").matches) return true;
    return localStorage.getItem(LS_PORTFOLIO_SIDEBAR_COLLAPSED) === "1";
  });
  const [chatOpen, setChatOpen] = useState(false);
  const assistantMessageCount = useProjectsListChatStore((s) =>
    countVisibleAssistantMessages(currentOrganizationId ? s.historyByOrg[currentOrganizationId] ?? [] : [])
  );
  const { hasUnread: hasUnreadChat, markSeen: markChatSeen } = useChatUnreadIndicator(assistantMessageCount, {
    ready: currentOrganizationId !== null,
    firstVisitKey: "projects-list",
    conversationKey: String(currentOrganizationId)
  });

  const toggleChat = (open: boolean) => {
    setChatOpen(open);
    markChatSeen();
  };

  useEffect(() => {
    if (isTabletLayout) setSidebarCollapsed(true);
  }, [isTabletLayout]);

  useEffect(() => {
    if (!isTabletLayout) localStorage.setItem(LS_PORTFOLIO_SIDEBAR_COLLAPSED, sidebarCollapsed ? "1" : "0");
  }, [sidebarCollapsed, isTabletLayout]);
  const canManageDimensions = hasAdminPrivileges(role);
  const canDuplicateProject = !!isLabs;
  const { data: savedFiltersList = [] } = useSavedFilters();

  const queryClient = useQueryClient();
  const { data, isLoading: isLoadingProjects, error, refetch } = useGetProjectsList();
  const { data: dimensions = [], isFetched: dimensionsLoaded } = useProjectDimensions();

  const {
    prefs,
    filters,
    persist,
    toggleSystemColumn,
    toggleDimensionVisibility,
    hideAllColumns,
    showAllColumns,
    toggleFilterValue,
    clearAllFilters
  } = usePortfolioPrefs({
    organizationId: currentOrganizationId,
    dimensions,
    dimensionsLoaded
  });

  const [duplicateSourceId, setDuplicateSourceId] = useState<string | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(null);
  const [filterRowOpen, setFilterRowOpen] = useState(
    () => Object.keys(prefs.savedFilters).length > 0
  );

  const sortField = useMemo(() => sortKeyToField(prefs.sortFieldKey, dimensions), [prefs.sortFieldKey, dimensions]);
  const deferredFilters = useDeferredValue(filters);
  const derived = usePortfolioDerivedList(projects, search, deferredFilters, sortField, prefs.sortDir);

  const activeFilterCount = Object.values(filters).reduce((n, s) => n + s.size, 0);
  const hiddenSet = new Set(prefs.hiddenDimensionIds);
  const hiddenSysSet = new Set(prefs.hiddenSystemColumns as TSystemColumn[]);

  const invalidUrlMutation = useMutation({
    mutationFn: updateCustomSnippetsUrl,
    onSuccess: () => {
      setMe({ customSnippetsUrl: null });
      refetch();
    }
  });

  const isLoading = isLoadingProjects || invalidUrlMutation.isPending;
  const isError = Boolean(error);
  const isInvalidUrlError = defineIsInvalidUrlError(error);

  const invalidateProjectsList = () => {
    queryClient.invalidateQueries({ queryKey: [GET_PROJECTS_LIST_KEY, currentOrganizationId] });
  };

  const handleDuplicateConfirm = async (orgId: number) => {
    if (!duplicateSourceId) return;
    const [, err] = await tryCatch<void, AxiosError<{ message: string }>>(
      duplicateProject(duplicateSourceId, orgId, { includeAllVersions: true })
    );
    if (err) {
      toast.error(err.response?.data.message || t("errors.duplicateFailed"));
      return;
    }
    toast.success(t("toasts.projectDuplicated"));
    queryClient.invalidateQueries({ queryKey: [GET_PROJECTS_LIST_KEY, currentOrganizationId] });
  };

  const confirmDeleteProject = async () => {
    if (!deleteTargetId) return;
    setDeletingProjectId(deleteTargetId);

    const target = projects.find((p) => p.id === deleteTargetId);
    const draftBriefId =
      target?.caseBriefId ??
      (deleteTargetId.startsWith("draft-") ? Number(deleteTargetId.slice("draft-".length)) : NaN);
    const isDraftWithBrief = Number.isFinite(draftBriefId);
    const action: Promise<unknown> = isDraftWithBrief
      ? $api.delete(`/chat/create-project-chat/case-brief/${draftBriefId}`)
      : archiveProject(deleteTargetId);
    const successMessage = isDraftWithBrief ? t("toasts.draftDeleted") : t("toasts.projectDeleted");
    const failureMessage = isDraftWithBrief ? t("errors.deleteDraftFailed") : t("errors.deleteProjectFailed");

    const [, err] = await tryCatch<unknown, AxiosError<{ message: string }>>(action);
    setDeletingProjectId(null);
    if (err) {
      toast.error(err.response?.data.message || failureMessage);
      return;
    }
    toast.success(successMessage);
    queryClient.invalidateQueries({ queryKey: [GET_PROJECTS_LIST_KEY, currentOrganizationId] });
  };

  const onSearchChange = (value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value.trim() === "") next.delete("search");
        else next.set("search", value);
        return next;
      },
      { replace: true }
    );
  };

  useEffect(() => {
    if (!data) return;
    setProjects(data);
  }, [data, setProjects]);

  const deleteTargetIsDraft = projects.find((p) => p.id === deleteTargetId)?.isDraft ?? false;

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[#f4f4f5] text-zinc-900">
      <div className="relative mx-auto flex w-full min-h-0 flex-1 flex-row gap-2 overflow-hidden p-2">
        <ProjectListSidebar
          collapsed={sidebarCollapsed}
          isOverlay={isTabletLayout}
          onToggleCollapsed={() => setSidebarCollapsed((v) => !v)}
        />
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white">
          <div
            data-testid="projects-toolbar"
            className="flex w-full shrink-0 flex-col gap-3 border-b-0 px-4 py-3 shadow-[inset_0_-1px_0_#f4f4f5] tablet:flex-row tablet:items-center tablet:justify-between tablet:gap-2"
          >
            <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
              <AppSearchInput
                data-testid="projects-page-search"
                placeholder={t("toolbar.searchPlaceholder")}
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full shrink-0 !opacity-100 !ring-0 hover:!ring-0 focus-within:!ring-0 sm:w-[232px] !h-8 !rounded-lg !border !border-innoveraZinc-150 !bg-white"
                classNameInput="text-[14px]"
              />
              <div className="hidden h-4 w-px shrink-0 bg-innoveraZinc-150 sm:block" />
              <SortPopover
                sortFieldKey={prefs.sortFieldKey}
                sortDir={prefs.sortDir}
                dimensions={dimensions}
                onSortFieldChange={(key) => persist({ sortFieldKey: key, sortDir: defaultSortDir(key) })}
                onSortDirChange={(dir) => persist({ sortDir: dir })}
              />
              <FilterToggleButton
                active={filterRowOpen}
                activeFilterCount={activeFilterCount}
                onToggle={() => setFilterRowOpen((v) => !v)}
              />
            </div>
            <div className="flex shrink-0 items-center justify-start gap-2 lg:justify-end">
              <PropertiesPopover
                dimensions={dimensions}
                hiddenDimensionIds={prefs.hiddenDimensionIds}
                hiddenSystemColumns={prefs.hiddenSystemColumns as TSystemColumn[]}
                canManageDimensions={canManageDimensions}
                orgId={currentOrganizationId}
                onToggleDimension={toggleDimensionVisibility}
                onToggleSystemColumn={toggleSystemColumn}
                onHideAll={hideAllColumns}
                onShowAll={showAllColumns}
                iconOnly
              />
              {hasEdit && (
                <>
                  <div className="h-4 w-px shrink-0 bg-innoveraZinc-150" />
                  <AppButton
                    data-testid="projects-page-create-project"
                    className="h-8 shrink-0 gap-1 !bg-innoveraZinc-900 !px-3 shadow-200"
                    onClick={() => navigate(getRouteCreateProject())}
                  >
                    <Plus className="h-4 w-4" />
                    {t("toolbar.newProject")}
                  </AppButton>
                </>
              )}
            </div>
          </div>

          <div
            className={cn(
              "w-full shrink-0 grid transition-[grid-template-rows] duration-200 ease-out",
              filterRowOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
            )}
          >
            <div className="overflow-hidden">
              <div
                data-testid="projects-filter-row"
                className="flex w-full flex-wrap items-center gap-2 border-b border-[#f4f4f5] px-4 py-3"
              >
                <SavedFiltersControl
                  activeFilters={prefs.savedFilters}
                  onApply={(filtersValue) => persist({ savedFilters: filtersValue })}
                />
                <span className="h-4 w-px shrink-0 bg-innoveraZinc-150" />
                <DimensionChipsRow
                  dimensions={dimensions}
                  projects={projects}
                  filters={filters}
                  onToggleFilterValue={toggleFilterValue}
                  onClearAll={clearAllFilters}
                />
                {!isFiltersEmpty(prefs.savedFilters) &&
                  findMatchingSavedFilter(savedFiltersList, prefs.savedFilters) === null && (
                    <SaveCurrentFiltersButton activeFilters={prefs.savedFilters} />
                  )}
              </div>
            </div>
          </div>

          {isError && !isInvalidUrlError && !isLoading && (
            <div className="flex flex-1 items-center justify-center bg-innoveraZinc-100">
              <div className="flex flex-col items-center gap-[22px] px-5 py-12">
                <div className="flex flex-col items-center gap-1">
                  <FigmaTypography variant="heading-3" color="zinc900">
                    {t("errors.loadFailedTitle")}
                  </FigmaTypography>
                  <FigmaTypography variant="body-2" color="zinc500" className="w-[244px] text-center">
                    {t("errors.loadFailedBody")}
                  </FigmaTypography>
                </div>
                <AppButton
                  appearance="outlined"
                  color="zinc150"
                  data-testid="projects-error-retry"
                  onClick={() => void refetch()}
                >
                  <RotateCw className="h-4 w-4" />
                  {t("errors.retry")}
                </AppButton>
              </div>
            </div>
          )}

          {isInvalidUrlError && !isLoading && (
            <PortfolioInvalidUrlState
              isPending={invalidUrlMutation.isPending}
              onClear={() => invalidUrlMutation.mutate({ customSnippetsUrl: null })}
            />
          )}

          {isLoading && (
            <div className="flex flex-1 items-center justify-center">
              <div className="w-12 h-12 border-2 border-gray-200 border-t-gray-500 rounded-full animate-spin" />
            </div>
          )}

          {!isError && !isLoading && (
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {projects.length === 0 && (
                <EmptyOrgState hasEdit={hasEdit} onAddProject={() => navigate(getRouteCreateProject())} />
              )}
              {projects.length > 0 && derived.length === 0 && (
                <NoProjectsFoundState
                  onClearAll={() =>
                    setSearchParams(
                      (prev) => {
                        const next = new URLSearchParams(prev);
                        next.delete("search");
                        for (const k of [...next.keys()]) {
                          if (filterKeyFromUrl(k) !== null) next.delete(k);
                        }
                        return next;
                      },
                      { replace: true }
                    )
                  }
                />
              )}
              {derived.length > 0 && (
                <>
                  {/* Outside the scroll container so the gap under the toolbar survives scrolling */}
                  <div className="h-2 shrink-0 bg-innoveraZinc-100" />
                  <div
                    className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-innoveraZinc-100 p-0"
                    onScroll={() => useProjectVersionsStore.getState().setOpenPopoverVersionGroupId(null)}
                  >
                    <ProjectsCards
                      projects={derived}
                      dimensions={dimensions}
                      hiddenDimensionIds={hiddenSet}
                      hiddenSystemColumns={hiddenSysSet}
                      hasEdit={hasEdit}
                      canDuplicateProject={canDuplicateProject}
                      organizationId={currentOrganizationId}
                      onListInvalidated={invalidateProjectsList}
                      onDuplicateProject={setDuplicateSourceId}
                      onDeleteProject={setDeleteTargetId}
                      deletingProjectId={deletingProjectId}
                    />
                    <div className="mt-auto">
                      <CommitHash className="pb-0" />
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {!isTabletLayout && <ChatPanel />}

        {isTabletLayout && (
          <>
            {!chatOpen && <ChatFab hasUnread={hasUnreadChat} onClick={() => toggleChat(true)} />}
            <div
              aria-hidden
              onClick={() => toggleChat(false)}
              className={cn(
                "fixed inset-0 z-30 bg-black/20 transition-opacity duration-300",
                chatOpen ? "opacity-100" : "pointer-events-none opacity-0"
              )}
            />
            <ChatPanel isOverlay isOpen={chatOpen} onClose={() => toggleChat(false)} />
          </>
        )}
      </div>

      <ConfirmationModal
        isOpen={deleteTargetId !== null}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={confirmDeleteProject}
        title={deleteTargetIsDraft ? t("deleteModal.draftTitle") : t("deleteModal.projectTitle")}
        description={deleteTargetIsDraft ? t("deleteModal.draftDescription") : t("deleteModal.projectDescription")}
        confirmText={t("deleteModal.deleteAction")}
        cancelText={t("common:cancel")}
        confirmButtonColor="red400"
      />

      <DuplicateProjectModal
        isOpen={duplicateSourceId !== null}
        onClose={() => setDuplicateSourceId(null)}
        onConfirm={handleDuplicateConfirm}
        currentOrganizationId={currentOrganizationId}
      />
    </div>
  );
}
